import EventBus from './event-bus'

/**
 * Toggle visibility of an element
 */
export default class Toggle {
  /**
   * Create new toggle
   * @param {Object} options Configuration
   * @param {(HTMLElement|NodeList)} options.trigger Single trigger or array of triggers
   * @param {(HTMLElement|string)} options.target ID of target element or target element itself
   * @param {string} [options.group] Optional group which will be closed (Default: target data-group attribute)
   * @param {string} [options.triggerClass="is-active"] CSS class that will be added to the trigger
   * @param {string} [options.targetClass="is-active"] CSS class that will be added to the target
   * @param {Function} [options.onOpen] Called when toggle is opened
   * @param {Function} [options.onClose] Called when toggle is closed
   */
  constructor (options) {
    this.config = Toggle.mergeSettings(options)

    // Abort if triggers don’t exist
    if (!this.config.trigger) {
      throw new Error('Trigger selector not working')
    }

    // Create array out of single trigger
    if (!this.config.trigger.length) {
      this.config.trigger = [this.config.trigger]
    }

    // If target is a string try getting element with string as ID
    if (typeof this.config.target === 'string') {
      this.config.target = document.querySelector('#' + this.config.target)
    }

    // Abort if target doesn’t exist
    if (!this.config.target) {
      throw new Error('Target selector not working')
    }

    // Check for ID on target element
    if (!this.config.target.id) {
      throw new Error('Target element requires an ID')
    }

    // Use target ID as toggle ID
    this.id = this.config.target.id

    // Try getting group from target data attribute
    if (!this.config.group) {
      this.config.group = this.config.target.dataset.group
    }

    // Set inital toggle state based on class on target
    if (this.config.target.classList.contains(this.config.targetClass)) {
      this.isOpen = true
    } else {
      this.isOpen = false
    }
  }

  /**
   * Merge user options with default options
   * @param {Object} options User options
   * @returns {Object} Customized settings
   */
  static mergeSettings (options) {
    const settings = {
      trigger: null,
      target: null,
      group: null,
      triggerClass: 'is-active',
      targetClass: 'is-active',
      onOpen: () => {},
      onClose: () => {}
    }

    // Object.assign looks nicer but would need a polyfill for IE
    for (const name in options) {
      settings[name] = options[name]
    }

    return settings
  }

  /**
   * Initialize toggle
   */
  init () {
    for (let i = 0; i < this.config.trigger.length; i++) {
      this.config.trigger[i].setAttribute('aria-controls', this.id)
      this.config.trigger[i].addEventListener('click', () => this.toggle())

      if (this.isOpen) {
        this.config.trigger[i].setAttribute('aria-expanded', 'true')
      } else {
        this.config.trigger[i].setAttribute('aria-expanded', 'false')
      }
    }

    if (this.isOpen) {
      this.config.target.setAttribute('aria-hidden', 'false')
    } else {
      this.config.target.setAttribute('aria-hidden', 'true')
    }

    // Listen to toggle event if this toggle is part of a group
    if (this.config.group) {
      EventBus.subscribe(`toggle-${this.config.group}`, id => {
        if (id !== this.id) this.close()
      })
    }
  }

  /**
   * Add class to trigger and target and set aria-attributes
   */
  open () {
    for (let i = 0; i < this.config.trigger.length; i++) {
      this.config.trigger[i].classList.add(this.config.triggerClass)
      this.config.trigger[i].setAttribute('aria-expanded', 'true')
    }

    this.config.target.classList.add(this.config.targetClass)
    this.config.target.setAttribute('aria-hidden', 'false')

    this.isOpen = true
    this.config.onOpen()

    // Publish toggle event if this toggle is part of a group
    if (this.config.group) {
      EventBus.publish(`toggle-${this.config.group}`, this.id)
    }
  }

  /**
   * Remove class from trigger and target and set aria-attributes
   */
  close () {
    for (let i = 0; i < this.config.trigger.length; i++) {
      this.config.trigger[i].classList.remove(this.config.triggerClass)
      this.config.trigger[i].setAttribute('aria-expanded', 'false')
    }

    this.config.target.classList.remove(this.config.targetClass)
    this.config.target.setAttribute('aria-hidden', 'true')

    this.isOpen = false
    this.config.onClose()
  }

  /**
   * Toggle open and close state
   */
  toggle () {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }
}



// WEBPACK FOOTER //
// ./src/functions/toggle.js